import { normalizeAngle, shortestAngleDelta } from './Body';

export type AlignmentTuning = {
  snapDistanceRatio: number;
  snapAngleDeg: number;
  softFieldRatio: number;
  softAngleDeg: number;
  softPull: number;
  softDamp: number;
  /** Always-on weak attraction toward the receiver (hint of destination). */
  ambientPull: number;
};

export type AlignmentSample = {
  centerDist: number;
  angleDelta: number;
  inSoftField: boolean;
  canSnap: boolean;
  physicsState: 'FREE' | 'FIELD' | 'SNAP' | 'DONE';
};

export function sampleAlignment(
  bodyX: number,
  bodyY: number,
  bodyAngle: number,
  receiverX: number,
  receiverY: number,
  receiverAngle: number,
  receiverSize: number,
  tuning: AlignmentTuning,
  snapping = false,
  solved = false,
): AlignmentSample {
  const centerDist = Math.hypot(receiverX - bodyX, receiverY - bodyY);
  const angleDelta = Math.abs(shortestAngleDelta(bodyAngle, receiverAngle));
  const snapDist = receiverSize * tuning.snapDistanceRatio;
  const softDist = receiverSize * tuning.softFieldRatio;
  const snapAngle = (tuning.snapAngleDeg * Math.PI) / 180;

  // Soft field is primarily distance-based so the "caught" feel arrives first.
  // Angle still gates the full snap.
  const inSoftField = centerDist <= softDist;
  const canSnap = centerDist <= snapDist && angleDelta <= snapAngle;

  let physicsState: AlignmentSample['physicsState'] = 'FREE';
  if (solved) physicsState = 'DONE';
  else if (snapping) physicsState = 'SNAP';
  else if (inSoftField) physicsState = 'FIELD';

  return {
    centerDist,
    angleDelta,
    inSoftField,
    canSnap,
    physicsState,
  };
}

export function softFieldForces(
  bodyX: number,
  bodyY: number,
  bodyAngle: number,
  receiverX: number,
  receiverY: number,
  receiverAngle: number,
  receiverSize: number,
  tuning: AlignmentTuning,
): { fx: number; fy: number; torque: number; damp: number } {
  const dx = receiverX - bodyX;
  const dy = receiverY - bodyY;
  const dist = Math.hypot(dx, dy) || 1;
  const softDist = Math.max(1, receiverSize * tuning.softFieldRatio);
  // Stronger pull deeper in the field, still gentle at the edge.
  const depth = 1 - Math.min(1, dist / softDist);
  const pull = tuning.softPull * (0.35 + depth * 0.65);

  const angleErr = shortestAngleDelta(bodyAngle, receiverAngle);
  const softAngle = (tuning.softAngleDeg * Math.PI) / 180;
  // Only meaningfully assist rotation once angle is vaguely in range.
  const angleAssist = Math.abs(angleErr) <= softAngle ? 1 : 0.25;

  return {
    fx: (dx / dist) * pull,
    fy: (dy / dist) * pull,
    torque: angleErr * pull * 0.045 * angleAssist,
    damp: tuning.softDamp * (0.55 + depth * 0.45),
  };
}

/** Gentle always-on tug so the destination is felt before entering the soft field. */
export function ambientReceiverPull(
  bodyX: number,
  bodyY: number,
  receiverX: number,
  receiverY: number,
  tuning: AlignmentTuning,
): { fx: number; fy: number } {
  const dx = receiverX - bodyX;
  const dy = receiverY - bodyY;
  const dist = Math.hypot(dx, dy) || 1;
  const pull = tuning.ambientPull;
  if (pull <= 0) return { fx: 0, fy: 0 };
  // Slightly stronger when farther so the hint carries across the split.
  const farBoost = Math.min(1.35, 0.65 + dist / 900);
  return {
    fx: (dx / dist) * pull * farBoost,
    fy: (dy / dist) * pull * farBoost,
  };
}

export { normalizeAngle, shortestAngleDelta };
